/**
 * Signature Renderer Utility
 * Renders signature text using canvas with script/handwriting fonts
 * and converts to data URL for PDF embedding
 */

export interface SignatureOptions {
  text: string;
  color: string;
  fontSize?: number;
  fontFamily?: string;
  width?: number;
  height?: number;
}

/**
 * Renders signature text to canvas and returns data URL
 * Uses script/handwriting font for authentic signature appearance
 */
export async function renderSignatureToDataURL(options: SignatureOptions): Promise<string> {
  const {
    text,
    color,
    fontSize = 44,
    fontFamily = "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
    width = 400,
    height = 80
  } = options;

  // Create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = width * 2; // 2x for better quality
  canvas.height = height * 2;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Set up canvas with transparent background
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Configure text rendering
  ctx.font = `italic ${fontSize * 2}px ${fontFamily}`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  // Enable better text rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the signature text
  ctx.fillText(text, 10, canvas.height / 2);

  // Convert to data URL
  return canvas.toDataURL('image/png');
}

/**
 * Measures the width of signature text for proper PDF positioning
 */
export function measureSignatureWidth(
  text: string,
  fontSize: number = 44,
  fontFamily: string = "'Brush Script MT', 'Lucida Handwriting', cursive"
): number {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    // Fallback estimate
    return text.length * fontSize * 0.5;
  }

  ctx.font = `italic ${fontSize}px ${fontFamily}`;
  const metrics = ctx.measureText(text);

  return metrics.width;
}

/**
 * Gets the signature font family used in the UI
 */
export function getSignatureFontFamily(): string {
  return "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive";
}
